import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { 
  Building2, TrendingUp, DollarSign, AlertTriangle, FileText,
  Receipt, Clock, RefreshCw, CreditCard, ShieldAlert, Calendar,
  CheckCircle, XCircle, AlertCircle, ChevronRight
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PostLeasingDashboardPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [malls, setMalls] = useState([]);
  const [selectedMall, setSelectedMall] = useState("all");
  const [dashboard, setDashboard] = useState(null);
  const [leases, setLeases] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [riskAlerts, setRiskAlerts] = useState({ alerts: [], total_alerts: 0 });
  const [compliance, setCompliance] = useState([]);
  
  // Invoice generation
  const [generateMonth, setGenerateMonth] = useState(
    new Date().toISOString().slice(0, 7)
  );
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetchMalls();
  }, []);

  useEffect(() => {
    if (token) {
      fetchDashboard();
      fetchRiskAlerts();
    }
  }, [token, selectedMall]);

  useEffect(() => {
    if (token && activeTab === "leases") {
      fetchLeases();
    } else if (token && activeTab === "invoices") {
      fetchInvoices();
    } else if (token && activeTab === "compliance") {
      fetchCompliance();
    }
  }, [token, activeTab, selectedMall]);

  const fetchMalls = async () => {
    try {
      const res = await axios.get(`${API}/api/malls`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMalls(res.data);
    } catch (err) {
      console.error("Failed to fetch malls:", err);
    }
  };

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const url = selectedMall === "all" 
        ? `${API}/api/post-leasing/dashboard`
        : `${API}/api/post-leasing/dashboard?mall_id=${selectedMall}`;
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(res.data);
    } catch (err) {
      console.error("Failed to fetch dashboard:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeases = async () => {
    try {
      const url = selectedMall === "all"
        ? `${API}/api/post-leasing/leases`
        : `${API}/api/post-leasing/leases?mall_id=${selectedMall}`;
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeases(res.data);
    } catch (err) {
      console.error("Failed to fetch leases:", err);
    }
  };

  const fetchInvoices = async () => {
    try {
      const res = await axios.get(`${API}/api/post-leasing/invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoices(res.data);
    } catch (err) {
      console.error("Failed to fetch invoices:", err);
    }
  };

  const fetchRiskAlerts = async () => {
    try {
      const res = await axios.get(`${API}/api/post-leasing/risk-alerts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRiskAlerts(res.data);
    } catch (err) {
      console.error("Failed to fetch risk alerts:", err);
    }
  };

  const fetchCompliance = async () => {
    try {
      const res = await axios.get(`${API}/api/post-leasing/compliance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompliance(res.data);
    } catch (err) {
      console.error("Failed to fetch compliance:", err);
    }
  };

  const generateInvoices = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(
        `${API}/api/post-leasing/invoices/generate-monthly?month=${generateMonth}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Generated ${res.data.generated} invoices for ${generateMonth}`);
      fetchInvoices();
    } catch (err) {
      toast.error("Failed to generate invoices");
    } finally {
      setGenerating(false);
    }
  };

  const formatCurrency = (val) => {
    if (!val) return "₹0";
    if (val >= 10000000) return `₹${(val / 10000000).toFixed(1)}Cr`;
    if (val >= 100000) return `₹${(val / 100000).toFixed(1)}L`;
    return `₹${val.toLocaleString()}`;
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: "default",
      expired: "destructive",
      terminated: "destructive",
      renewed: "secondary",
      paid: "default",
      overdue: "destructive",
      partially_paid: "outline",
      generated: "secondary",
      valid: "default",
      expiring_soon: "outline",
    };
    return <Badge variant={variants[status] || "secondary"} className="capitalize">{status?.replace(/_/g, ' ')}</Badge>;
  };

  if (loading && !dashboard) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="post-leasing-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold">Post-Leasing Dashboard</h1>
          <p className="text-muted-foreground">Lease lifecycle, billing, compliance & risk management</p>
        </div>
        <Select value={selectedMall} onValueChange={setSelectedMall}>
          <SelectTrigger className="w-[250px]" data-testid="mall-selector">
            <SelectValue placeholder="Select Mall" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Malls</SelectItem>
            {malls.map(mall => (
              <SelectItem key={mall.id} value={mall.id}>{mall.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">NOI (Annual)</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-500">{formatCurrency(dashboard?.noi)}</div>
            <p className="text-xs text-muted-foreground">Net Operating Income</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Collection Efficiency</CardTitle>
            <CreditCard className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dashboard?.collection_efficiency_percent || 0}%</div>
            <Progress value={dashboard?.collection_efficiency_percent || 0} className="mt-2 h-2" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Outstanding</CardTitle>
            <Receipt className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-500">{formatCurrency(dashboard?.outstanding_amount)}</div>
            <p className="text-xs text-muted-foreground">Pending collections</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Expiring Soon</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-500">{dashboard?.expiry_next_90_days || 0}</div>
            <p className="text-xs text-muted-foreground">Leases in next 90 days</p>
          </CardContent>
        </Card>
      </div>

      {/* Risk Alerts Banner */}
      {riskAlerts.critical > 0 && (
        <Card className="border-red-500 bg-red-500/10">
          <CardContent className="flex items-center justify-between py-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-6 w-6 text-red-500" />
              <div>
                <h4 className="font-semibold text-red-500">{riskAlerts.critical} Critical Alerts</h4>
                <p className="text-sm text-muted-foreground">Require immediate attention</p>
              </div>
            </div>
            <Button variant="destructive" size="sm" onClick={() => setActiveTab("alerts")}>
              View Alerts <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="leases" data-testid="tab-leases">Leases</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="tab-invoices">Invoices</TabsTrigger>
          <TabsTrigger value="compliance" data-testid="tab-compliance">Compliance</TabsTrigger>
          <TabsTrigger value="alerts" data-testid="tab-alerts">
            Alerts {riskAlerts.total_alerts > 0 && <Badge variant="destructive" className="ml-1">{riskAlerts.total_alerts}</Badge>}
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-6">
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Financial Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">EBITDA (Annual)</span>
                  <span className="font-medium">{formatCurrency(dashboard?.ebitda)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Rental Yield</span>
                  <span className="font-medium">{dashboard?.rental_yield_percent}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Occupancy</span>
                  <span className="font-medium">{dashboard?.occupancy_percent}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Revenue Concentration (Top 5)</span>
                  <span className="font-medium">{dashboard?.revenue_concentration_top5_percent}%</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ShieldAlert className="h-5 w-5" />
                  Risk Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-red-500" />
                      <span>Critical</span>
                    </div>
                    <Badge variant="destructive">{riskAlerts.critical || 0}</Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-500" />
                      <span>Warning</span>
                    </div>
                    <Badge variant="outline">{riskAlerts.warning || 0}</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Leases Tab */}
        <TabsContent value="leases" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Active Leases ({leases.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {leases.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No leases found. Create leases from the Proposal Builder.</p>
              ) : (
                <div className="space-y-3">
                  {leases.slice(0, 10).map(lease => (
                    <div key={lease.id} className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                      <div>
                        <div className="font-medium">{lease.tenant_name || "Unknown Tenant"}</div>
                        <div className="text-sm text-muted-foreground">
                          {lease.unit_number} • {lease.mall_name}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-medium">{formatCurrency(lease.monthly_rent)}/month</div>
                        <div className="text-sm text-muted-foreground flex items-center gap-2">
                          {getStatusBadge(lease.status)}
                          {lease.days_to_expiry > 0 && lease.days_to_expiry <= 90 && (
                            <span className="text-amber-500">{lease.days_to_expiry}d left</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Invoices Tab */}
        <TabsContent value="invoices" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Receipt className="h-5 w-5" />
                Invoices ({invoices.length})
              </CardTitle>
              <Dialog>
                <DialogTrigger asChild>
                  <Button>Generate Monthly Invoices</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Generate Monthly Invoices</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Month</Label>
                      <Input 
                        type="month" 
                        value={generateMonth}
                        onChange={(e) => setGenerateMonth(e.target.value)}
                      />
                    </div>
                    <Button onClick={generateInvoices} disabled={generating} className="w-full">
                      {generating ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : null}
                      Generate Invoices
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {invoices.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No invoices found. Generate monthly invoices to get started.</p>
              ) : (
                <div className="space-y-3">
                  {invoices.slice(0, 10).map(inv => (
                    <div key={inv.id} className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                      <div>
                        <div className="font-medium">{inv.tenant_name}</div>
                        <div className="text-sm text-muted-foreground">
                          {inv.unit_number} • {inv.month}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-medium">{formatCurrency(inv.total_due)}</div>
                        <div className="text-sm flex items-center gap-2">
                          {getStatusBadge(inv.status)}
                          {inv.balance > 0 && (
                            <span className="text-red-500">Bal: {formatCurrency(inv.balance)}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Compliance Tab */}
        <TabsContent value="compliance" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShieldAlert className="h-5 w-5" />
                Compliance Records ({compliance.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {compliance.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No compliance records found.</p>
              ) : (
                <div className="space-y-3">
                  {compliance.map(rec => (
                    <div key={rec.id} className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                      <div>
                        <div className="font-medium capitalize">{rec.document_type?.replace(/_/g, ' ')}</div>
                        <div className="text-sm text-muted-foreground">{rec.tenant_name}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-muted-foreground">Expires: {rec.expiry_date}</div>
                        {getStatusBadge(rec.status)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Alerts Tab */}
        <TabsContent value="alerts" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                Risk Alerts ({riskAlerts.total_alerts})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {riskAlerts.alerts?.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
                  <p className="text-muted-foreground">No risk alerts. Everything looks good!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {riskAlerts.alerts?.map((alert, idx) => (
                    <div 
                      key={idx} 
                      className={`flex items-center justify-between p-4 rounded-lg ${
                        alert.severity === 'critical' ? 'bg-red-500/10 border border-red-500/20' : 'bg-amber-500/10 border border-amber-500/20'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {alert.severity === 'critical' ? (
                          <XCircle className="h-5 w-5 text-red-500" />
                        ) : (
                          <AlertTriangle className="h-5 w-5 text-amber-500" />
                        )}
                        <div>
                          <div className="font-medium capitalize">{alert.type?.replace(/_/g, ' ')}</div>
                          <div className="text-sm text-muted-foreground">{alert.message}</div>
                        </div>
                      </div>
                      <Badge variant={alert.severity === 'critical' ? 'destructive' : 'outline'}>
                        {alert.severity}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
